<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { formatNumberToFixed } from '@/helper/intl'

const props = defineProps<{
  modelValue: string | number
  readonly?: boolean
  id: string
  placeholder: string
  suffix: string
  catchFocus?: boolean
  showSuffixOnEmpty?: boolean
  resetValue?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void
  (e: 'change', val: string): void
}>()

const currentSuffix = ref<string>('')
const input = ref<HTMLInputElement | null>(null)

const showResetButton = computed((): boolean => {
  return !!props.resetValue && props.resetValue !== props.modelValue
})

onMounted(() => {
  if (props.modelValue) {
    updateValue()
  }
})

watch(
  () => props.modelValue,
  (val: string | number, oldVal: string | number) => {
    if (val !== oldVal) {
      fixSuffix(val)
    }
  }
)

watch(
  () => props.suffix,
  () => {
    fixSuffix(props.modelValue)
  }
)

currentSuffix.value = props.showSuffixOnEmpty ? props.suffix : ''

fixSuffix(props.modelValue)

function fixSuffix(val: string | number): void {
  const onEmptySuffix = props.showSuffixOnEmpty ? props.suffix : ''
  if (val == '' || val == undefined) {
    currentSuffix.value = onEmptySuffix
  } else {
    currentSuffix.value = props.suffix
  }
}

function updateValue(): void {
  if (input.value) {
    const val = formatNumberToFixed(input.value!.value)
    // valueModel doesn't sync with input value
    input.value!.value = val
    emit('update:modelValue', val)

    fixSuffix(val)
  }
}

function blur(): void {
  emit('change', input.value!.value)
}

function resetClicked(): void {
  if (props.resetValue) {
    emit('update:modelValue', props.resetValue)
  }
}
</script>

<template>
  <div class="inputsuffix__wrapper">
    <input
      :id="id"
      ref="input"
      :class="{ firstfocus: catchFocus, 'is--readonly': readonly }"
      :placeholder="placeholder"
      :readonly="readonly"
      :value="modelValue"
      type="text"
      @blur="blur"
      @change="updateValue"
      @input="updateValue"
    >
    <button v-if="showResetButton" class="icon-btn input__reset" @click="resetClicked">
      <i class="ic-reset" />
    </button>
    <div class="inputsuffix__suffixcontainer">
      <span>{{ modelValue }}</span>
      <span>{{ currentSuffix }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.inputsuffix {
  &__wrapper {
    position: relative;

    input {
      z-index: 1;
      background-color: transparent;
      position: relative;
      width: 100%;
      padding: 0 rem-calc(12);

      &.is--readonly {
        @include themify($themes) {
          background: $readonlyInputColor;
        }
      }
    }

    &--full {
      flex: 1;
    }

    @include media('<tablet') {
      width: 100%;
    }
  }

  &__suffixcontainer {
    z-index: 1;
    pointer-events: none;
    position: absolute;
    top: 0;
    left: rem-calc(4);
    bottom: 0;
    right: 0;
    overflow: hidden;
    text-overflow: clip;
    line-height: 1;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    padding: 0 rem-calc(8);

    & > span:first-child {
      color: transparent;
    }

    & > span:nth-child(2) {
      position: relative;
      bottom: -1px; // Fix flex centering causing suffix to be a bit too high
      margin-left: rem-calc(2.5);

      @-moz-document url-prefix() {
        bottom: 0; // Fix flex centering causing suffix to be a bit too high
      }
    }
  }
}
</style>
